import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { useMemo } from 'react'
import AddToCalendarHOC from 'react-add-to-calendar-hoc'
import SvgRight from '#/assets/images/icons/chevron-right.svg'
import SvgDate from '#/assets/images/metrics/date.svg'
import DateTime from '#/components/DateTime'
import Modal, { ModalProps } from '#/components/Modal'
import { host } from '#/configs'
import { viewUrl } from '#/utils/properties'
import { Section } from '../common'

dayjs.extend(duration)

type Props = {
  property: TopHap.Property
  openHouses: TopHap.OpenHouse[]
}

type ItemButtonProps = {
  item: TopHap.OpenHouse
  onClick: () => void
}

type ItemModalProps = Omit<ModalProps, 'visible' | 'onClose'> & {
  isOpen: boolean
  onRequestClose: () => void
}

const ItemButton: React.FC<ItemButtonProps> = ({ item, onClick }) => {
  const startTime = dayjs(item.OpenHouseStartTime)
  const endTime = dayjs(item.OpenHouseEndTime)
  return (
    <div
      className='inline-flex cursor-pointer snap-center items-center space-x-5 whitespace-normal rounded-xl bg-white p-4 shadow-md'
      onClick={onClick}
    >
      <div className='flex flex-col'>
        <div className='flex items-center space-x-2'>
          <SvgDate className='text-grtay-400 h-5 w-5' />
          <DateTime className='font-medium'>{startTime.format('dddd, MMM D')}</DateTime>
        </div>
        <div className='my-1 ml-7 text-sm'>
          <DateTime>{startTime.format('hh:mm a')}</DateTime>
          -
          <DateTime>{endTime.format('hh:mm a')}</DateTime>
        </div>
      </div>
      <SvgRight className='mx-3 mb-2 h-3 w-3 text-gray-400' />
    </div>
  )
}

const ItemModal: React.FC<ItemModalProps> = ({
  isOpen,
  onRequestClose,
  children,
  ...props
}) => (
  <Modal visible={isOpen} onClose={onRequestClose} {...props}>
    <div className='w-56 text-xl font-semibold'>Add to Calendar</div>
    <div className='mt-2'>{children}</div>
  </Modal>
)

const Item: React.FC<{ item: TopHap.OpenHouse; property: TopHap.Property }> = ({
  property,
  item
}) => {
  const event = useMemo(() => {
    const startTime = dayjs(item.OpenHouseStartTime)
    const endTime = dayjs(item.OpenHouseEndTime)
    return {
      title: `${property.displayAddress} Open House`,
      description:
        property.displayAddress +
        ', ' +
        property.displayRegion +
        (item.OpenHouseRemarks ? `, ${item.OpenHouseRemarks}` : '') +
        `, ${host}${viewUrl(property.id, property.address)}`,
      location: property.displayAddress + ', ' + property.displayRegion,
      startDatetime: startTime.format('YYYYMMDDTHHmmss'),
      endDatetime: endTime.format('YYYYMMDDTHHmmss'),
      duration: dayjs.duration(endTime.diff(startTime)).asHours()
    }
  }, [item, property])

  const AddToCalendarModal = useMemo(
    () => AddToCalendarHOC(ItemButton, ItemModal),
    []
  )

  return (
    <AddToCalendarModal
      buttonProps={{ item }}
      className='inline-block'
      event={event}
      linkProps={{ className: 'block hover:text-primary hover:bg-gray-100 px-4 py-3' }}
    />
  )
}

const OpenHouses: React.FC<Props> = ({ property, openHouses }) => (
  <Section
    description={(
      <>
        The property is open to prospective {property.RentFlag ? 'tenants' : 'buyers'} on {' '}
        {
          openHouses.map((openHouse, index) => (
            <DateTime key={index}>
              {(dayjs(openHouse.OpenHouseStartTime).format('MMM DD, YYYY')) + ((index === openHouses.length - 2) ? (openHouses.length >= 3 ? ', and ' : ' and ') : (index !== openHouses.length - 1 ? ', ' : ''))}
            </DateTime>
          ))
        }.
      </>
    )}
    id='openhouse'
    title='Open House'
  >
    {openHouses.map((e, index) => (
      <Item key={index} item={e} property={property} />
    ))}
  </Section>
)

export default OpenHouses
